import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import {
  GeneralConfirmationModalData,
  GeneralConfirmationModalResult,
  Invoice,
  InvoiceStatus,
  PetInsuranceData
} from "../../../models";
import { Router } from "@angular/router";
import {
  invoicePdfErrorMessageKeys,
  parseDateCreated,
  qrCodeGenerationString,
  recipientCity,
  recipientHouseNumber,
  recipientIbanFormated,
  recipientName,
  recipientStreet,
  recipientZIP
} from "../../../utils";
import { ApiHttpService } from "../../../services/api-http.service";
import { DialogService } from "../../../services/dialog.service";
import { DocumentViewerService } from "../../../services/document-viewer.service";
import {
  GeneralConfirmationModalComponent
} from "../../../components/general-confirmation-modal/general-confirmation-modal.component";
import { TranslateService } from "@ngx-translate/core";
import * as QRCode from 'qrcode';

@Component({
  selector: 'cpf-invoice-details',
  templateUrl: './invoice-details.component.html',
  styleUrls: ['./invoice-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class InvoiceDetailsComponent {
  @Input()
  public invoice!: Invoice;

  @Input()
  public petData!: PetInsuranceData;
  @Input() showBackButton = true;

  public downloadingPdf = false;

  constructor(private router: Router,
              private dialogService: DialogService,
              private translate: TranslateService,
              private documentViewerService: DocumentViewerService,
              private apiHttpService: ApiHttpService) {
  }

  async generateQRCode() {
    const qrCodeData = await QRCode.toDataURL(qrCodeGenerationString(this.invoice, this.petData));

    const dialogRef = this.dialogService.openDialog(GeneralConfirmationModalComponent, {
      data: {
        title: this.translate.instant('invoice-details.qr-code-title'),
        confirmButton: GeneralConfirmationModalResult.Confirm,
        confirmButtonText: 'OK',
        isImage: true,
        imageSrc: qrCodeData,
      } as GeneralConfirmationModalData
    })

    dialogRef.afterClosed().subscribe(() => {})
  }

  transformDateCreated() {
    return this.invoice.dateCreated instanceof Date ? this.invoice.dateCreated : parseDateCreated(this.invoice.dateCreated);
  }

  parseDueDate(dueDate: string): Date {
    return parseDateCreated(dueDate);
  }

  checkInvoiceStatus(invoice: Invoice): 'paid' | 'outstanding' | 'future' | 'outdated' | 'aggregated' {
    switch (invoice.invoiceStatus) {
      case InvoiceStatus.PAID: return 'paid';
      case InvoiceStatus.OUTSTANDING: return 'outstanding';
      case InvoiceStatus.FUTURE: return 'future';
      case InvoiceStatus.OUTDATED: return 'outdated';
      case InvoiceStatus.AGGREGATED: return 'aggregated';
    }
  }

  formatInvoiceStatus(invoice: Invoice): string {
    return `invoice-details.status.${invoice.invoiceStatus}`;
  }

  openInvoicePdf() {
    const invoiceId = this.invoice.bexio_invoice_id;
    if (!this.invoice.bexio_pdf_available || !invoiceId || this.downloadingPdf) {
      return;
    }

    // The PDF no longer travels with the contract list, so it is fetched here.
    this.downloadingPdf = true;
    this.documentViewerService.openRemoteBase64Document(
      () => this.apiHttpService.getInvoicePdf(invoiceId),
      { fileName: this.invoice.invoiceNumber, errorMessageKeys: invoicePdfErrorMessageKeys }
    ).finally(() => this.downloadingPdf = false);
  }

  navigateBack() {
    this.router.navigate(['calingo', 'pet', 'invoice']);
  }

  protected readonly recipientIbanFormated = recipientIbanFormated;
  protected readonly recipientName = recipientName;
  protected readonly recipientStreet = recipientStreet;
  protected readonly recipientHouseNumber = recipientHouseNumber;
  protected readonly recipientZIP = recipientZIP;
  protected readonly recipientCity = recipientCity;
}
